import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import classNames from 'classnames';
import { hasFeatureFlags } from 'utils/features';
import StickySidebar from 'components/shared/StickySidebar';
import ExpandableList, {
  ExpandableListStaticHeader,
} from 'components/ui/ExpandableList';
import CustomGroups from './CustomGroups';
import SmartGroups from './SmartGroups';
import GroupsAutoComplete from './GroupsAutoComplete';
import {
  hasFeature,
  hasPermission,
  canEditGroups,
  hasEditSmartGroupsPermissions,
} from '../../../selectors';

const LeftSidebar = ({
  groups,
  activeGroup,
  updateActiveGroup,
  sortedGroups,
  createNewRuleBasedGroup,
  canEditSmartGroups,
  canManageGroups,
  canManageAllGroups,
  hasSystemGeneratedGroups,
  canAddPeople,
  toggleGroupsList,
  groupsListVisible,
  updateMyOrganization,
  myOrganization,
}) => {
  useEffect(() => {
    const groupsList = document.querySelector('.LeftSidebar-groupsList');

    if (groupsList) {
      const newHeight =
        window.innerHeight - groupsList.getBoundingClientRect().top - 10;

      groupsList.style.height = newHeight + 'px';
    }
  }, [groupsListVisible]);

  const hasUnificationFeatureFlag = hasFeatureFlags([
    'readiness-enable-unification',
  ]);
  // Pull out the sorted groups.
  const customGroups = get(sortedGroups, 'Group', []);
  const smartGroups = get(sortedGroups, 'Group::Smart', []);
  const systemGeneratedGroups = get(sortedGroups, 'Group::SystemGenerated', []);

  const showSmartGroups =
    canEditSmartGroups ||
    canManageAllGroups ||
    (canManageGroups && !!smartGroups.length);
  const showCustomGroups = canAddPeople || !!customGroups.length;
  const showSystemGeneratedGroups =
    hasSystemGeneratedGroups && !!systemGeneratedGroups.length;
  const sidebarClasses = classNames('LeftSidebar', {
    'LeftSidebar--unify': hasUnificationFeatureFlag,
    'LeftSidebar--modal': groupsListVisible,
    'bg-brand-gray-lightest': hasUnificationFeatureFlag,
    'bg-white': !hasUnificationFeatureFlag,
  });

  const clearActiveGroup = () => {
    updateMyOrganization(false);
    updateActiveGroup(null);
    if (groupsListVisible) {
      toggleGroupsList();
    }
  };

  const showMyOrganization = () => {
    updateActiveGroup(null);
    updateMyOrganization(true);
  };

  return (
    <StickySidebar className={sidebarClasses} disable={groupsListVisible}>
      <div className="LeftSidebar-search">
        <GroupsAutoComplete
          groups={groups}
          activeGroup={activeGroup}
          updateActiveGroup={updateActiveGroup}
          toggleGroupsList={toggleGroupsList}
          groupsListVisible={groupsListVisible}
        />
      </div>
      <ExpandableList className="LeftSidebar-groupsList">
        <ExpandableListStaticHeader
          className={classNames(
            'CustomColor--borderColorLeft',
            'NestedList-header',
            {
              'NestedList-header--active':
                activeGroup === null &&
                !myOrganization &&
                !hasUnificationFeatureFlag,
              'c-color-primary':
                activeGroup === null &&
                !myOrganization &&
                hasUnificationFeatureFlag,
            },
          )}
          onClick={clearActiveGroup}
          active={activeGroup === null && !myOrganization}
        >
          {t('groups.everyone.name')}
        </ExpandableListStaticHeader>
        {showSystemGeneratedGroups && (
          <ExpandableListStaticHeader
            className={classNames(
              'CustomColor--borderColorLeft',
              'mt-16',
              'NestedList-header',
              {
                'NestedList-header--active':
                  activeGroup === null &&
                  myOrganization &&
                  !hasUnificationFeatureFlag,
                'c-color-primary':
                  activeGroup === null &&
                  myOrganization &&
                  hasUnificationFeatureFlag,
              },
            )}
            onClick={showMyOrganization}
            tooltip={t('groups.system_generated.description')}
            active={activeGroup === null && myOrganization}
          >
            {t('groups.system_generated.title')}
          </ExpandableListStaticHeader>
        )}
        {showSmartGroups && (
          <SmartGroups
            groups={smartGroups}
            customGroups={customGroups}
            activeGroup={activeGroup}
            updateActiveGroup={updateActiveGroup}
            createNewRuleBasedGroup={createNewRuleBasedGroup}
            canEditSmartGroups={canEditSmartGroups}
            toggleGroupsList={toggleGroupsList}
            groupsListVisible={groupsListVisible}
            updateMyOrganization={updateMyOrganization}
          />
        )}

        {showCustomGroups && (
          <CustomGroups
            groups={customGroups}
            activeGroup={activeGroup}
            updateActiveGroup={updateActiveGroup}
            canAddPeople={canAddPeople}
            toggleGroupsList={toggleGroupsList}
            groupsListVisible={groupsListVisible}
            updateMyOrganization={updateMyOrganization}
          />
        )}
      </ExpandableList>
    </StickySidebar>
  );
};

const mapStateToProps = state => ({
  activeGroup: state.filter.activeGroup,
  myOrganization: state.filter.myOrganization,
  canAddPeople: canEditGroups(state),
  canManageGroups: hasPermission(state, 'manageGroups'),
  canEditSmartGroups: hasEditSmartGroupsPermissions(state),
  canManageAllGroups: hasPermission(state, 'manageAllGroups'),
  hasSystemGeneratedGroups: hasFeature(state, 'systemGeneratedGroups'),
});

export default connect(mapStateToProps)(LeftSidebar);
